import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import LandingPage from "@/pages/LandingPage";
import DemoPage from "@/pages/DemoPage";
import InvoicesPage from "@/pages/InvoicesPage";
import LoginPage from "@/pages/LoginPage";
import OnboardingPage from "@/pages/OnboardingPage";
import Dashboard from "@/pages/Dashboard";
import Editor from "@/pages/Editor";
import Presentation from "@/pages/Presentation";
import PresenterView from "@/pages/PresenterView";
import SetlistPresentation from "@/pages/SetlistPresentation";
import StreamOutput from "@/pages/StreamOutput";
import CommunityPage from "@/pages/CommunityPage";
import PhotoEnhancementPage from "@/pages/PhotoEnhancementPage";
import AdminDashboard from "@/pages/AdminDashboard";
import ServiceCalendar from "@/pages/ServiceCalendar";
import ScannerPage from "@/pages/ScannerPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import MessagesPage from "@/pages/MessagesPage";
import DownloadsPage from "@/pages/DownloadsPage";
import PreachKit from "@/pages/PreachKit";
import ApiKeys from "@/pages/ApiKeys";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AcceptableUsePage from "@/pages/AcceptableUsePage";
import PricingPage from "@/pages/PricingPage";
import CollaboratorsPage from "@/pages/CollaboratorsPage";
import PublicSharedDeck from "@/pages/PublicSharedDeck";
import EmbedSharedDeck from "@/pages/EmbedSharedDeck";
import HelpBot from "@/components/HelpBot";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminQuickSwitcher from "@/components/admin/AdminQuickSwitcher";
import BetaDisclaimer from "@/components/BetaDisclaimer";
import InstallPWABanner from "@/components/InstallPWABanner";
import OnboardingTour from "@/components/OnboardingTour";

// AppRouter handles session_id detection for Google OAuth callback
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // This MUST be checked synchronously during render, not in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        
        {/* Legal pages - Public */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/acceptable-use" element={<AcceptableUsePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/invoices" element={
          <ProtectedRoute>
            <InvoicesPage />
          </ProtectedRoute>
        } />
        <Route path="/collaborators" element={<CollaboratorsPage />} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/editor/:projectId" element={
          <ProtectedRoute>
            <Editor />
          </ProtectedRoute>
        } />
        <Route path="/present/:projectId" element={
          <ProtectedRoute>
            <Presentation />
          </ProtectedRoute>
        } />
        <Route path="/presenter/:projectId" element={
          <ProtectedRoute>
            <PresenterView />
          </ProtectedRoute>
        } />
        <Route path="/setlist/:setlistId" element={
          <ProtectedRoute>
            <SetlistPresentation />
          </ProtectedRoute>
        } />
        <Route path="/community" element={
          <ProtectedRoute>
            <CommunityPage />
          </ProtectedRoute>
        } />
        <Route path="/community/:communityId/photos" element={
          <ProtectedRoute>
            <PhotoEnhancementPage />
          </ProtectedRoute>
        } />
        <Route path="/community/:communityId/calendar" element={
          <ProtectedRoute>
            <ServiceCalendar />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/scanner" element={
          <ProtectedRoute>
            <ScannerPage />
          </ProtectedRoute>
        } />
        <Route path="/community/:communityId/appointments" element={
          <ProtectedRoute>
            <AppointmentsPage />
          </ProtectedRoute>
        } />
        <Route path="/appointments" element={
          <ProtectedRoute>
            <AppointmentsPage />
          </ProtectedRoute>
        } />
        <Route path="/messages" element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        } />
        <Route path="/messages/:conversationId" element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        } />
        <Route path="/preach-kit" element={
          <ProtectedRoute>
            <PreachKit />
          </ProtectedRoute>
        } />
        <Route path="/integrations" element={
          <ProtectedRoute>
            <ApiKeys />
          </ProtectedRoute>
        } />
        {/* Backward-compat redirect for old PulpitAI route */}
        <Route path="/pulpit-ai" element={
          <ProtectedRoute>
            <PreachKit />
          </ProtectedRoute>
        } />
        
        {/* Stream output - no auth required (for OBS) */}
        <Route path="/stream/:projectId" element={<StreamOutput />} />

        {/* Public shared decks - no auth required */}
        <Route path="/s/:slug" element={<PublicSharedDeck />} />
        {/* Embeddable chromeless variant (for iframes) */}
        <Route path="/embed/s/:slug" element={<EmbedSharedDeck />} />
        
        {/* Downloads page - public */}
        <Route path="/download" element={<DownloadsPage />} />
      </Routes>
      
      {/* Global Help Bot - shows on all pages except stream output and auth pages */}
      <Routes>
        <Route path="/stream/*" element={null} />
        <Route path="/s/*" element={null} />
        <Route path="/embed/*" element={null} />
        <Route path="/login" element={null} />
        <Route path="/onboarding" element={null} />
        <Route path="*" element={<HelpBot />} />
      </Routes>
      
      {/* Admin Quick Switcher - shows for admin users on authenticated pages */}
      <Routes>
        <Route path="/stream/*" element={null} />
        <Route path="/login" element={null} />
        <Route path="/onboarding" element={null} />
        <Route path="/" element={null} />
        <Route path="/present/*" element={null} />
        <Route path="/presenter/*" element={null} />
        <Route path="/setlist/*" element={null} />
        <Route path="*" element={<AdminQuickSwitcher />} />
      </Routes>
      
      {/* Beta Disclaimer - shows once for logged in users */}
      <BetaDisclaimer />

      {/* PWA install prompt — Chromium/Android + iOS Safari */}
      <InstallPWABanner />

      {/* First-run onboarding walkthrough */}
      <OnboardingTour />
    </>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="dark">
      <LanguageProvider>
        <AuthProvider>
          <div className="App">
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <Toaster richColors position="top-right" />
          </div>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
